import React, { useMemo, useRef, useState } from "react";
import "./calculator-runner.scss";
import { InputNumber, Segmented, Button, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBookMedical, faCircleCheck, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { Toast } from "../../../utils/SwalUti";
import { addVisitScore, updateVisitScore } from "../../../service/api/visit";
import { buildMeter } from "../../../utils/rheumatology/bands";

// Resolve a field label/unit that may be a plain string or a fn(options).
const resolve = (val, opts) => (typeof val === "function" ? val(opts) : val);

// Seed form values from a saved run where present, otherwise from field defaults.
// Walking definition.fields rather than the saved object means a config that
// changed since the score was saved can't feed stale keys back in.
const seedValues = (definition, savedInputs) => {
    const v = {};
    (definition.fields || []).forEach((f) => {
        const prev = savedInputs?.values?.[f.key];
        if (prev !== undefined && prev !== null && !Number.isNaN(prev)) v[f.key] = prev;
        else if (f.default !== undefined) v[f.key] = f.default;
    });
    return v;
};

const variantOf = (definition, opts) =>
    (definition.options || [])
        .map((opt) => opts[opt.key])
        .filter(Boolean)
        .join(" / ");

const CalculatorRunner = ({ definition, visitDetails, existingScore, calculatorScores, onSaved, onBack }) => {
    // A score already saved on this visit for this calculator — its inputs seed the form.
    // Identity is calculator + variant: saving updates the row matching the CURRENT
    // variant (ESR vs CRP etc.), and a variant with no saved row gets a new entry, so
    // e.g. DAS28 (ESR) and DAS28 (CRP) can both live on one visit and both print.
    const saved = existingScore?.inputs;

    const initialOpts = useMemo(() => {
        const o = {};
        (definition.options || []).forEach((opt) => {
            const prev = saved?.options?.[opt.key];
            const legal = prev !== undefined && (!opt.choices || opt.choices.includes(prev));
            o[opt.key] = legal ? prev : opt.default ?? opt.choices?.[0];
        });
        return o;
    }, [definition, saved]);

    const initialValues = useMemo(() => seedValues(definition, saved), [definition, saved]);

    const [opts, setOpts] = useState(initialOpts);
    const [values, setValues] = useState(initialValues);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Guards against a second click landing before the disabled state has flushed.
    const submittingRef = useRef(false);

    const visibleFields = (definition.fields || []).filter(
        (f) => !f.showIf || f.showIf(opts)
    );

    const filledCount = visibleFields.filter((f) => {
        const v = values[f.key];
        return v !== undefined && v !== null && v !== "";
    }).length;
    const allFilled = filledCount === visibleFields.length;

    const numericValues = useMemo(() => {
        const out = {};
        Object.keys(values).forEach((k) => {
            out[k] = Number(values[k]);
        });
        return out;
    }, [values]);

    const score = allFilled ? definition.compute(numericValues, opts) : null;
    const validScore = score !== null && score !== undefined && !Number.isNaN(score);
    const band = validScore ? definition.interpret(score, opts) : null;
    const rounded = validScore ? Math.round(score * 100) / 100 : null;

    const meter = useMemo(() => {
        if (!definition.bands) return null;
        return buildMeter(validScore ? score : null, definition.bands(opts));
    }, [definition, opts, score, validScore]);

    const variantLabel = variantOf(definition, opts);

    // The row a save would write: the opened row if the variant still matches (keeps
    // pinned legacy duplicates individually editable), else this calculator's row for
    // the currently selected variant, else none (save creates a new row).
    const targetScore = useMemo(() => {
        if (existingScore && (existingScore.variant || "") === variantLabel) return existingScore;
        return (calculatorScores || []).find((r) => (r.variant || "") === variantLabel) || null;
    }, [existingScore, calculatorScores, variantLabel]);
    const isEditing = Boolean(targetScore?._id);

    // Variant switch: load that variant's saved run if one exists; otherwise keep the
    // shared fields (joint counts, VAS — same exam either way) and clear only fields
    // whose meaning depends on the option (e.g. the ESR/CRP marker — mm/h vs mg/L
    // are not interchangeable numbers).
    const onOptionChange = (key, val) => {
        const nextOpts = { ...opts, [key]: val };
        setOpts(nextOpts);
        const nextVariant = variantOf(definition, nextOpts);
        const row = (existingScore && (existingScore.variant || "") === nextVariant)
            ? existingScore
            : (calculatorScores || []).find((r) => (r.variant || "") === nextVariant);
        if (row?.inputs) {
            setValues(seedValues(definition, row.inputs));
            return;
        }
        setValues((prev) => {
            const next = { ...prev };
            (definition.fields || []).forEach((f) => {
                const optionDependent = [f.label, f.unit, f.min, f.max, f.hint].some(
                    (x) => typeof x === "function"
                );
                if (optionDependent) delete next[f.key];
            });
            return next;
        });
    };

    const onSave = () => {
        if (!validScore || !visitDetails?._id || submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);
        const payload = {
            calculatorId: definition.id,
            name: definition.name,
            variant: variantLabel,
            score: rounded,
            category: band?.label,
            inputs: { options: opts, values: numericValues },
        };
        const request = isEditing
            ? updateVisitScore(targetScore._id, payload, visitDetails._id)
            : addVisitScore(payload, visitDetails._id);

        request
            .then((res) => {
                if (res.status === 200) {
                    onSaved?.(res.data);
                    Toast.fire({
                        icon: "success",
                        title: `${definition.name} ${isEditing ? "updated" : "saved to visit"}`,
                    });
                }
            })
            .catch(() => {
                Toast.fire({ icon: "error", title: `Could not ${isEditing ? "update" : "save"} score` });
            })
            .finally(() => {
                submittingRef.current = false;
                setIsSubmitting(false);
            });
    };

    const progressPct = visibleFields.length
        ? Math.round((filledCount / visibleFields.length) * 100)
        : 0;

    return (
        <div className="calculator-runner">
            {onBack && (
                <button type="button" className="back-btn" onClick={onBack}>
                    <FontAwesomeIcon icon={faArrowLeft} /> All calculators
                </button>
            )}

            <div className="runner-head">
                <div className="titles">
                    <h4>{definition.name}</h4>
                    <p>{definition.subtitle}</p>
                </div>
                {/* Shows the value as saved, so the hero above reads as the "after". */}
                {isEditing && (
                    <span className="editing-pill">
                        <FontAwesomeIcon icon={faPenToSquare} /> Saved: {targetScore.score}
                    </span>
                )}
            </div>

            {/* Result hero */}
            <div className="result-hero">
                <div className="result-top">
                    <div className="score-block">
                        <span className="score-value" style={validScore ? { color: band.color } : {}}>
                            {validScore ? rounded : "—"}
                        </span>
                        <span className="score-label">Score</span>
                    </div>
                    {validScore ? (
                        <span className="band-pill" style={{ background: band.color }}>
                            {band.label}
                        </span>
                    ) : (
                        <span className="band-pill placeholder">
                            {progressPct}% complete
                        </span>
                    )}
                </div>

                {meter && (
                    <div className="meter">
                        <div className="meter-track">
                            {meter.segments.map((seg, i) => (
                                <div
                                    key={i}
                                    className="meter-seg"
                                    style={{ width: `${seg.widthPct}%`, background: seg.color }}
                                    title={seg.label}
                                />
                            ))}
                            {validScore && meter.markerPct !== null && (
                                <div className="meter-marker" style={{ left: `${meter.markerPct}%` }} />
                            )}
                        </div>
                        <div className="meter-legend">
                            {meter.segments.map((seg, i) => (
                                <span key={i} className="legend-item">
                                    <i style={{ background: seg.color }} />
                                    {seg.label}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {(definition.options || []).map((opt) => (
                <div className="option-row" key={opt.key}>
                    <label>{opt.label}</label>
                    <Segmented
                        options={opt.choices}
                        value={opts[opt.key]}
                        onChange={(val) => onOptionChange(opt.key, val)}
                    />
                </div>
            ))}

            {definition.intro && <p className="fields-intro">{definition.intro}</p>}

            <div className={`fields ${definition.layout === "compact" ? "" : "stacked"}`}>
                {visibleFields.map((f, idx) => {
                    const has = values[f.key] !== undefined && values[f.key] !== null && values[f.key] !== "";
                    const hint = resolve(f.hint, opts);
                    const fmin = resolve(f.min, opts);
                    const fmax = resolve(f.max, opts);
                    const unit = resolve(f.unit, opts);
                    const isToggle = f.type === "toggle";
                    const prev = visibleFields[idx - 1];
                    const showSection = f.section && (!prev || prev.section !== f.section);
                    return (
                        <React.Fragment key={f.key}>
                            {showSection && <div className="field-section-title">{f.section}</div>}
                            <div className={`field-row ${isToggle ? "toggle" : ""} ${has && !isToggle ? "filled" : ""}`}>
                                <div className="field-main">
                                    <label>{resolve(f.label, opts)}</label>
                                    {isToggle ? (
                                        <Segmented
                                            size="small"
                                            className="toggle-input"
                                            options={[
                                                { label: "No", value: 0 },
                                                { label: "Yes", value: 1 },
                                            ]}
                                            value={values[f.key] ?? 0}
                                            onChange={(val) => setValues({ ...values, [f.key]: val })}
                                        />
                                    ) : f.type === "select" ? (
                                        <Select
                                            className="field-input"
                                            value={values[f.key]}
                                            onChange={(val) => setValues({ ...values, [f.key]: val })}
                                            options={f.choices}
                                        />
                                    ) : (
                                        <InputNumber
                                            className="field-input"
                                            min={fmin}
                                            max={fmax}
                                            step={f.step || 1}
                                            value={values[f.key]}
                                            onChange={(val) => setValues({ ...values, [f.key]: val })}
                                            placeholder={
                                                fmin !== undefined && fmax !== undefined ? `${fmin}–${fmax}` : ""
                                            }
                                            addonAfter={unit || undefined}
                                        />
                                    )}
                                </div>
                                {hint && <div className="field-hint">{hint}</div>}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            <Button
                type="primary"
                className="save-btn"
                disabled={!validScore || isSubmitting}
                loading={isSubmitting}
                onClick={onSave}
                icon={<FontAwesomeIcon icon={isEditing ? faPenToSquare : faCircleCheck} />}
            >
                {isEditing ? "Update score" : "Save to visit"}
            </Button>

            <div className="info-block">
                <div className="info-head">
                    <FontAwesomeIcon icon={faBookMedical} className="mr-2" />
                    Instructions and references
                </div>
                <div className="about">
                    <p>{definition.about}</p>
                    {definition.references?.length ? (
                        <ul>
                            {definition.references.map((r, i) => (
                                <li key={i}>{r}</li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default CalculatorRunner;
